import { Award, Heart, Hammer, Users } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

export const About = () => {
  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            About <span className="text-crimson-600">Hernandez Remodeling LLC</span>
          </h2>
          <p className="text-lg text-gray-600">
            A family-owned remodeling company built on hard work, honesty, and pride in craftsmanship.
          </p>
        </div>

        {/* Company Story */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-900">Our Story</h3>
            <p className="text-gray-600 leading-relaxed">
              Hernandez Remodeling LLC is a family-owned remodeling company based in Gordonsville, Virginia, 
              proudly serving homeowners throughout Central Virginia, including Charlottesville, Louisa, Orange, 
              and surrounding communities.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Founded in 2015, the company was built on a simple principle: hard work, honesty, and pride in 
              craftsmanship. What began as a small family-driven operation has grown into a trusted local 
              remodeling business known for delivering reliable, high-quality work.
            </p>
            <p className="text-gray-600 leading-relaxed">
              From the beginning, the Hernandez family believed that every home should reflect the people who 
              live in it. With years of hands-on experience in construction and remodeling, the team focused 
              on helping homeowners transform their spaces while maintaining affordable pricing and strong 
              relationships with clients.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Today, Hernandez Remodeling LLC continues to expand across Central Virginia, completing projects 
              ranging from kitchen and bathroom remodels to roofing, flooring, painting, drywall, decks, and 
              home additions. The company has built its reputation on dependable service, attention to detail, 
              and treating every project as if it were their own home.
            </p>
          </div>

          <div className="relative h-full">
            <img 
              src="https://customer-assets.emergentagent.com/job_remodel-pro-6/artifacts/eqxgxfo1_House2.jpg" 
              alt="Our Work" 
              className="rounded-2xl shadow-xl w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Mission & Vision Tabs */}
        <div className="mb-16">
          <Tabs defaultValue="mission" className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-2 h-auto">
              <TabsTrigger value="mission" className="text-lg py-3">Mission</TabsTrigger>
              <TabsTrigger value="vision" className="text-lg py-3">Vision</TabsTrigger>
            </TabsList>
            <TabsContent value="mission" className="mt-8">
              <Card className="border-2">
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 leading-relaxed text-center">
                    Our mission is to transform homes into beautiful, comfortable, and functional spaces by 
                    providing high-quality remodeling services, honest communication, and dependable workmanship 
                    that exceeds expectations.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="vision" className="mt-8">
              <Card className="border-2">
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 leading-relaxed text-center">
                    To become one of the most trusted remodeling companies in Central Virginia, known for quality 
                    craftsmanship, strong client relationships, and projects that stand the test of time.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Core Values</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center hover:shadow-xl transition-shadow border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-crimson-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-crimson-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Family & Community</h4>
                <p className="text-gray-600">
                  As a family-run company, we treat every client like family and take pride in improving homes 
                  across Central Virginia.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-crimson-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-crimson-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Integrity</h4>
                <p className="text-gray-600">
                  Honesty, transparency, and reliability guide every project—from the first estimate to the 
                  final walkthrough.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-crimson-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Hammer className="w-8 h-8 text-crimson-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Craftsmanship</h4>
                <p className="text-gray-600">
                  Quality work and attention to detail are at the center of every job, ensuring lasting results.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-crimson-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-crimson-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Customer Commitment</h4>
                <p className="text-gray-600">
                  Every project is customized to meet the client's vision, budget, and timeline while delivering 
                  exceptional results.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* What Sets Us Apart */}
        <div className="bg-crimson-600 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold text-center mb-12">What Sets Us Apart</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <h4 className="text-xl font-bold mb-2">Locally Owned</h4>
              <p className="text-crimson-100">Family operated and proudly serving Central Virginia</p>
            </div>
            <div className="text-center">
              <h4 className="text-xl font-bold mb-2">Honest Pricing</h4>
              <p className="text-crimson-100">Clear communication and transparent estimates</p>
            </div>
            <div className="text-center">
              <h4 className="text-xl font-bold mb-2">Skilled Craftsmanship</h4>
              <p className="text-crimson-100">Expert work across many remodeling services</p>
            </div>
            <div className="text-center">
              <h4 className="text-xl font-bold mb-2">Strong Relationships</h4>
              <p className="text-crimson-100">Built on trust with clients and community</p>
            </div>
            <div className="text-center">
              <h4 className="text-xl font-bold mb-2">Experienced Team</h4>
              <p className="text-crimson-100">Professional service you can depend on</p>
            </div>
            <div className="text-center">
              <h4 className="text-xl font-bold mb-2">Quality Guaranteed</h4>
              <p className="text-crimson-100">We treat every project like our own home</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
